/**
 * SyncIndicator — small pill badge showing offline queue status.
 * Shows nothing when idle. Yellow pill when pending, pulse when syncing, red when failed.
 */
import { useState, useEffect, useCallback } from 'react';
import { Cloud, CloudOff, RefreshCw, AlertTriangle } from 'lucide-react';
import { subscribeSyncStatus, flush, clearFailed, type SyncStatus } from '@/lib/offlineQueue';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';

export function SyncIndicator() {
    const [status, setStatus] = useState<SyncStatus>('idle');
    const [count, setCount] = useState(0);

    useEffect(() => {
        const unsub = subscribeSyncStatus((s, c) => {
            setStatus(s);
            setCount(c);
        });
        return unsub;
    }, []);

    // Listen for sync-complete events
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as { synced: number; failed: number };
            if (detail.synced > 0) {
                toast.success(`Синхронизировано: ${detail.synced}`, {
                    description: detail.failed > 0 ? `${detail.failed} не удалось` : undefined,
                });
            }
        };
        window.addEventListener('offline-queue-synced', handler);
        return () => window.removeEventListener('offline-queue-synced', handler);
    }, []);

    const handleRetry = useCallback(async () => {
        if (status === 'has-failed') {
            await clearFailed();
            toast.info('Ошибочные записи очищены');
        } else {
            const result = await flush();
            if (result.synced > 0) {
                toast.success(`Синхронизировано: ${result.synced}`);
            } else {
                toast.info('Нет данных для синхронизации');
            }
        }
    }, [status]);

    if (status === 'idle') return null;

    return (
        <button
            onClick={handleRetry}
            className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all',
                'border backdrop-blur-sm',
                status === 'syncing' && 'bg-blue-500/15 text-blue-400 border-blue-500/30 animate-pulse',
                status === 'has-pending' && 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
                status === 'has-failed' && 'bg-red-500/15 text-red-400 border-red-500/30',
            )}
            title={
                status === 'syncing' ? 'Синхронизация...'
                    : status === 'has-pending' ? `${count} изменений ожидают отправки`
                        : `${count} ошибок синхронизации — нажмите для очистки`
            }
        >
            {status === 'syncing' && <RefreshCw className="w-3 h-3 animate-spin" />}
            {status === 'has-pending' && <Cloud className="w-3 h-3" />}
            {status === 'has-failed' && <AlertTriangle className="w-3 h-3" />}
            <span>
                {status === 'syncing' ? 'Синхронизация...'
                    : status === 'has-pending' ? `↑ ${count}`
                        : `! ${count}`}
            </span>
        </button>
    );
}